import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, BarChart2, TrendingUp, Layers, UploadCloud, Image, Video, Users, UserPlus, BookOpen, Download, Calendar } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { getProAnalytics } from '@/lib/pro-team-api';
import { eventStatusLabel, statusPillClasses } from '@/lib/event-labels';

export default function ProAnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErrorCode(null); setErrorMsg(null);
    try { setData(await getProAnalytics()); }
    catch (e) { setErrorCode(e?.code || null); setErrorMsg(e?.message || 'Could not load analytics.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isPro = (user?.account_type || '').toUpperCase() === 'PROFESSIONAL';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="pro-analytics-screen">
      <div className="flex items-center justify-between mb-1">
        <button onClick={() => navigate(-1)} className="p-1 -ml-1 hover:bg-[#F3F5F8] rounded-lg transition-colors" data-testid="pro-analytics-back-btn"><ChevronLeft className="w-5 h-5 text-[#0B1B46]" /></button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="pro-analytics-title">Cross-event analytics</h1>
        <div className="w-5" />
      </div>
      <p className="text-sm text-[#5B6472] mb-4" data-testid="pro-analytics-subtitle">Privacy-safe totals across every event you host. Counts only, no attendee details.</p>

      {loading ? (
        <div className="flex justify-center py-10" data-testid="pro-analytics-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : errorCode === 'PROFESSIONAL_ONLY' || (!isPro && errorMsg) ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-analytics-upsell">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">Professional accounts only</p>
          <p className="text-sm text-[#5B6472] mb-3">Cross-event analytics are part of ALLVIEWS PRO. Switch account type to Professional and start a subscription to unlock.</p>
          <button onClick={() => navigate('/host-premium')} className="h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm hover:bg-[#0F55CC] transition-colors" data-testid="pro-analytics-upsell-btn">See PRO plans</button>
        </div>
      ) : errorMsg ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-analytics-error">
          <p className="text-sm font-semibold text-[#DC2626]">{errorMsg}</p>
          <button onClick={load} className="mt-2 h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm" data-testid="pro-analytics-retry-btn">Retry</button>
        </div>
      ) : data ? (
        <div className="flex flex-col gap-4">
          <SummaryGrid data={data} />
          <WeeklyTrend weekly={data.weekly_uploads || []} />
          <PerEventTable rows={data.per_event || []} onOpen={(id) => navigate(`/host-dashboard?event_id=${id}`)} />
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Summary grid ---------- */
const ICON_MAP = { 'upload-cloud': UploadCloud, image: Image, video: Video, users: Users, 'user-plus': UserPlus, 'book-open': BookOpen, download: Download, calendar: Calendar };

function SummaryGrid({ data }) {
  const s = data.summary;
  const cells = [
    { label: 'Total uploads', value: s.uploads_total, icon: 'upload-cloud', tid: 'summary-uploads' },
    { label: 'Photos', value: s.uploads_photos, icon: 'image', tid: 'summary-photos' },
    { label: 'Videos', value: s.uploads_videos, icon: 'video', tid: 'summary-videos' },
    { label: 'Verified guests', value: s.contributors_verified, icon: 'users', tid: 'summary-contributors-verified' },
    { label: 'Pending approvals', value: s.contributors_pending, icon: 'user-plus', tid: 'summary-pending' },
    { label: 'Lookbooks published', value: s.lookbooks_published, icon: 'book-open', tid: 'summary-lookbooks' },
    { label: 'Frame exports', value: s.frame_exports_run, icon: 'download', tid: 'summary-exports' },
    { label: 'Events', value: Object.values(s.events_by_status || {}).reduce((a, b) => a + b, 0), icon: 'calendar', tid: 'summary-events' },
  ];

  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-analytics-summary-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 className="w-4 h-4 text-[#0B1B46]" />
        <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">All-time</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cells.map((c) => {
          const Ic = ICON_MAP[c.icon] || Calendar;
          return (
            <div key={c.tid} className="bg-[#F3F5F8] rounded-lg p-3 flex flex-col items-start gap-1" data-testid={c.tid}>
              <Ic className="w-3.5 h-3.5 text-[#146BFF]" />
              <span className="text-lg font-extrabold text-[#0B1B46] truncate max-w-full">{(c.value ?? 0).toLocaleString()}</span>
              <span className="text-[10px] text-[#5B6472] uppercase tracking-wide leading-tight">{c.label}</span>
            </div>
          );
        })}
      </div>
      {Object.keys(s.events_by_status || {}).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {Object.entries(s.events_by_status).map(([k, v]) => (
            <span key={k} className="px-2.5 py-1 bg-[#F3F5F8] rounded-full text-[11px] font-semibold text-[#5B6472]" data-testid={`summary-status-${k}`}>
              {eventStatusLabel(k)} &middot; {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- 12-week upload trend ---------- */
function WeeklyTrend({ weekly }) {
  const max = useMemo(() => Math.max(1, ...weekly.map((w) => w.count)), [weekly]);
  const total = useMemo(() => weekly.reduce((a, w) => a + w.count, 0), [weekly]);

  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-analytics-trend-card">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-[#0B1B46]" />
        <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Uploads &middot; last 12 weeks</span>
      </div>
      <p className="text-sm text-[#5B6472] mb-3" data-testid="pro-analytics-trend-total">
        {total.toLocaleString()} uploads in the last {weekly.length} weeks.
      </p>
      <div className="flex items-end gap-1 h-24">
        {weekly.map((w, i) => {
          const h = Math.max(3, Math.round((w.count / max) * 88));
          const isLast = i === weekly.length - 1;
          return (
            <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`trend-week-${i}`}>
              <div style={{ height: h }} className={`w-full rounded-sm ${isLast ? 'bg-[#146BFF]' : 'bg-[#146BFF]/30'}`} />
              <span className="text-[9px] text-[#5B6472] mt-1 tabular-nums" data-testid={`trend-week-count-${i}`}>{w.count}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-[#5B6472]">12w ago</span>
        <span className="text-[10px] text-[#5B6472]">This week</span>
      </div>
    </div>
  );
}

/* ---------- Per-event comparison table ---------- */
function PerEventTable({ rows, onOpen }) {
  if (!rows.length) {
    return (
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-analytics-per-event-empty">
        <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">No events yet</p>
        <p className="text-sm text-[#5B6472]">Once you create your first event, its stats will appear here.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="pro-analytics-per-event-card">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-[#0B1B46]" />
        <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Per-event comparison</span>
      </div>
      <div className="flex flex-col divide-y divide-[#E3E8EF]">
        {rows.map((r) => (
          <button key={r.event_id} onClick={() => onOpen(r.event_id)}
            className="flex items-center gap-3 py-3 text-left hover:bg-[#F9FAFB] -mx-1 px-1 rounded transition-colors"
            data-testid={`pro-analytics-row-${r.event_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0B1B46] truncate">{r.name || 'Untitled event'}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusPillClasses(r.status)}`} data-testid={`pro-analytics-row-status-${r.event_id}`}>
                  <span>&#8226;</span> {eventStatusLabel(r.status)}
                </span>
                {r.start_at && <span className="text-[11px] text-[#5B6472]">{new Date(r.start_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <MiniStat label="Photos" value={r.uploads_photos} tid={`pro-analytics-row-photos-${r.event_id}`} />
              <MiniStat label="Videos" value={r.uploads_videos} tid={`pro-analytics-row-videos-${r.event_id}`} />
              <MiniStat label="Guests" value={r.contributors} tid={`pro-analytics-row-guests-${r.event_id}`} />
              {r.pending_approvals > 0 && <MiniStat label="Pending" value={r.pending_approvals} warn tid={`pro-analytics-row-pending-${r.event_id}`} />}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, value, warn, tid }) {
  return (
    <div className="text-center min-w-[36px]" data-testid={tid}>
      <p className={`text-sm font-extrabold ${warn ? 'text-[#B86500]' : 'text-[#0B1B46]'}`}>{(value ?? 0).toLocaleString()}</p>
      <p className="text-[9px] text-[#5B6472] uppercase tracking-wide">{label}</p>
    </div>
  );
}
